// src/services/assistant/streamMultiAgent.ts
/**
 * Stream Multi-Agent - Pure function for SSE streaming orchestration
 * 
 * Replaces the class-based AIStreamManager with a simpler functional approach.
 * 
 * Key improvements:
 * - Uses token batching for reduced re-renders
 * - Uses Web Worker for SSE parsing (when available)
 * - Single function instead of class with methods
 * - Cleaner error handling
 */

import AssistantService, { type ChatMessage } from "@/services/assistant/AssistantService";
import { useAssistantStore } from "@/stores/assistantStore";
import { tokenBatcher } from "./TokenBatcher";
import type { AgentId, ToolState, ChartSpec, UIGroup, SSEEvent } from "@/types/assistant";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface StreamOptions {
  onStart?: (conversationId: string) => void;
  onEnd?: (conversationId: string) => void;
  /** Agent mode: "quick_chat" for fast single-agent, undefined for deep research */
  agent?: "quick_chat" | undefined;
  /** Auth token for API calls */
  authToken?: string | undefined;
  /** Unified context for thesis-aware AI */
  context?: Record<string, unknown> | undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Event Dispatcher
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Dispatch a parsed SSE event to the appropriate store action
 */
function dispatchEvent(
  event: SSEEvent,
  conversationId: string,
  streamId: string,
  opts: StreamOptions
): void {
  const store = useAssistantStore.getState();

  switch (event.event) {
    case "stream.start":
      store.setStreaming(conversationId, true);
      opts.onStart?.(conversationId);
      break;

    case "agent.plan":
      store.setPlannedAgents(conversationId, event.data.agents);
      break;

    case "agent.start":
      store.setAgentStart(
        conversationId,
        event.data.agent,
        event.data.display_name,
        event.data.ui.group ?? "analysis",
        streamId
      );
      break;

    case "agent.draft":
      store.setAgentDraft(conversationId, event.data.agent, event.data.text);
      break;

    case "agent.done":
      store.setAgentDone(
        conversationId,
        event.data.agent,
        event.data.ok,
        event.data.irrelevant,
        event.data.error
      );
      break;

    case "token":
      // Use batching for high-frequency token updates
      tokenBatcher.add(
        conversationId,
        event.data.agent,
        event.data.delta,
        event.data.channel,
        streamId
      );
      break;

    case "thinking":
      store.appendThinking(
        conversationId,
        event.data.agent,
        event.data.delta,
        event.data.text
      );
      break;

    case "tool":
      if (event.data.phase === "calling") {
        const tool: ToolState = {
          id: event.data.id,
          name: event.data.name,
          phase: "calling",
          argsPreview: event.data.args_preview,
          server: event.data.server,
          url: event.data.url,
        };
        store.addToolCalling(conversationId, event.data.agent, streamId, tool);
      } else {
        const tool: ToolState = {
          id: event.data.id,
          name: event.data.name,
          phase: "completed",
          ok: event.data.ok,
          elapsedMs: event.data.elapsed_ms,
          resultPreview: event.data.result_preview,
          payload: event.data.payload,
          server: event.data.server,
          url: event.data.url,
        };
        store.completeToolCall(conversationId, event.data.agent, streamId, tool);
      }
      break;

    case "chart.dsl":
      store.addCharts(conversationId, event.data.charts);
      break;

    case "message":
      store.setFinalMessage(conversationId, event.data.text, streamId);
      break;

    case "stream.end":
      // Flush any pending tokens before ending
      tokenBatcher.flushImmediate();
      store.setStreaming(conversationId, false);
      store.removeController(conversationId);
      opts.onEnd?.(conversationId);
      break;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Function
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Stream a multi-agent response
 * 
 * @param service - AssistantService instance for making the API call
 * @param conversationId - The conversation ID
 * @param history - Chat message history
 * @param opts - Stream options (callbacks, auth, mode)
 */
export async function streamMultiAgent(
  service: AssistantService,
  conversationId: string,
  history: ChatMessage[],
  opts: StreamOptions
): Promise<void> {
  const store = useAssistantStore.getState();
  const controller = new AbortController();
  store.setController(conversationId, controller);

  const streamId = crypto.randomUUID();

  // Initialize stream state in Zustand
  store.initStream(conversationId, history, streamId);

  try {
    console.log("[streamMultiAgent] Calling service.streamMultiAgent");
    await service.streamMultiAgent(
      history,
      {
        onStreamStart: () => {
          dispatchEvent(
            { event: "stream.start", data: { conversation_id: conversationId } },
            conversationId,
            streamId,
            opts
          );
        },

        onAgentPlan: (agents) => {
          dispatchEvent(
            { event: "agent.plan", data: { agents } },
            conversationId,
            streamId,
            opts
          );
        },

        onAgentStart: (agentId: AgentId, displayName: string, uiGroup: UIGroup) => {
          dispatchEvent(
            {
              event: "agent.start",
              data: { agent: agentId, display_name: displayName, ui: { group: uiGroup } },
            },
            conversationId,
            streamId,
            opts
          );
        },

        onAgentDraft: (agentId: AgentId, text: string) => {
          dispatchEvent(
            { event: "agent.draft", data: { agent: agentId, text } },
            conversationId,
            streamId,
            opts
          );
        },

        onAgentDone: (agentId: AgentId, ok: boolean, irrelevant: boolean, error?: string) => {
          dispatchEvent(
            { 
              event: "agent.done", 
              data: { 
                agent: agentId, 
                ok, 
                irrelevant,
                ...(error !== undefined && { error }),
              } 
            },
            conversationId,
            streamId,
            opts
          );
        },

        onToken: (delta: string, agentId: AgentId, channel: "draft" | "final") => {
          dispatchEvent(
            { event: "token", data: { delta, agent: agentId, channel } },
            conversationId,
            streamId,
            opts
          );
        },

        onThinking: (delta: string | undefined, text: string | undefined, agentId: AgentId) => {
          dispatchEvent(
            { 
              event: "thinking", 
              data: { 
                ...(delta !== undefined && { delta }),
                ...(text !== undefined && { text }),
                agent: agentId, 
                channel: "draft" as const,
              } 
            },
            conversationId,
            streamId,
            opts
          );
        },

        onToolCalling: (id, name, agentId, argsPreview, args, server, url) => {
          dispatchEvent(
            {
              event: "tool",
              data: {
                phase: "calling" as const,
                id,
                name,
                ...(argsPreview !== undefined && { args_preview: argsPreview }),
                ...(args !== undefined && { args }),
                server: server ?? "",
                url: url ?? "",
                agent: agentId,
                channel: "draft" as const,
              },
            },
            conversationId,
            streamId,
            opts
          );
        },

        onToolCompleted: (id, name, agentId, ok, elapsedMs, resultPreview, payload, server, url) => {
          dispatchEvent(
            {
              event: "tool",
              data: {
                phase: "completed" as const,
                id,
                name,
                ok,
                ...(elapsedMs !== undefined && { elapsed_ms: elapsedMs }),
                ...(resultPreview !== undefined && { result_preview: resultPreview }),
                ...(payload !== undefined && { payload }),
                server: server ?? "",
                url: url ?? "",
                agent: agentId,
                channel: "draft" as const,
              },
            },
            conversationId,
            streamId,
            opts
          );
        },

        onCharts: (charts: ChartSpec[]) => {
          dispatchEvent(
            { event: "chart.dsl", data: { agent: "chart", charts } },
            conversationId,
            streamId,
            opts
          );
        },

        onFinalMessage: (text: string) => {
          dispatchEvent(
            { event: "message", data: { role: "assistant", text, channel: "final" as const } },
            conversationId,
            streamId,
            opts
          );
        },

        onStreamEnd: () => {
          dispatchEvent(
            { event: "stream.end", data: {} },
            conversationId,
            streamId,
            opts
          );
        },

        signal: controller.signal,
      },
      {
        conversationId,
        ...(opts.authToken && { authToken: opts.authToken }),
        ...(opts.agent && { agent: opts.agent }),
        ...(opts.context && { context: opts.context }),
      }
    );
  } catch (err) {
    // Flush any pending tokens before error handling
    tokenBatcher.flushImmediate();
    tokenBatcher.clearConversation(conversationId);
    
    store.handleStreamError(conversationId, streamId);
    opts.onEnd?.(conversationId);
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Utility Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Stop a streaming conversation
 */
export function stopStream(conversationId: string): void {
  tokenBatcher.clearConversation(conversationId);
  useAssistantStore.getState().stopStream(conversationId);
}

/**
 * Set baseline messages for a conversation
 */
export function setBaseline(conversationId: string, messages: ChatMessage[]): void {
  useAssistantStore.getState().setBaseline(conversationId, messages);
}

/**
 * Get current state for a conversation
 */
export function getState(conversationId: string) {
  return useAssistantStore.getState().getConversation(conversationId);
}

export default streamMultiAgent;

